// Apolo IDE — S2 · TaskGraph (DAG real sobre Tasks com depends_on).
// @arch:pure-engine
//
// Fecha o gap do S2-03-PLAN (E1-WS1-T02/T03): o board/decomposer atuais usam um
// `canParallelize:boolean` chato (sem arestas reais). Aqui está o GRAFO de verdade:
// ordem topológica, detecção de ciclo, próximos executáveis (deps todas green) e
// ondas (waves) de independentes. JS puro, sem I/O, sem imports — domínio puro
// (Dependency Rule), testável isolado. O executor task-driven consome isto.
//
// Task aceito: { id:string, depends_on?:string[], status?:string }. `depends_on`
// referencia ids de OUTROS tasks; ids desconhecidos são ignorados (decomposer pode
// emitir refs frouxas — o grafo é robusto a isso, não explode).

/** Status que contam como "concluído com sucesso" (libera dependentes). */
export const GREEN_STATUS = Object.freeze(['done', 'green', 'oracle_pass']);
/** Status terminais (não re-executam). WSX: 'blocked' (task que não pôde executar —
 * permissão/timeout) é terminal p/ ESTE run: não re-roda em loop nem libera dependentes. */
export const TERMINAL_STATUS = Object.freeze(['done', 'green', 'oracle_pass', 'failed', 'blocked']);

/** @param {Array<{id:string,depends_on?:string[]}>} tasks */
function indexById(tasks) {
  const map = new Map();
  for (const t of Array.isArray(tasks) ? tasks : []) {
    if (t && typeof t.id === 'string' && t.id) map.set(t.id, t);
  }
  return map;
}

/** Arestas reais (ignora deps pra ids inexistentes — robusto a refs frouxas). */
function depsOf(task, byId) {
  const raw = Array.isArray(task?.depends_on) ? task.depends_on : [];
  return raw.filter((d) => typeof d === 'string' && byId.has(d) && d !== task.id);
}

/**
 * Ordem topológica (Kahn). Retorna `string[]` de ids ou `null` se houver ciclo.
 * Determinístico: desempata pela ordem de entrada.
 */
export function topoOrder(tasks) {
  const byId = indexById(tasks);
  const ids = [...byId.keys()];
  const indeg = new Map(ids.map((id) => [id, 0]));
  const out = new Map(ids.map((id) => [id, []]));
  for (const id of ids) {
    for (const d of depsOf(byId.get(id), byId)) {
      indeg.set(id, indeg.get(id) + 1);
      out.get(d).push(id);
    }
  }
  // fila estável: ids sem dependência, na ordem de entrada
  const queue = ids.filter((id) => indeg.get(id) === 0);
  const order = [];
  while (queue.length) {
    const id = queue.shift();
    order.push(id);
    for (const next of out.get(id)) {
      indeg.set(next, indeg.get(next) - 1);
      if (indeg.get(next) === 0) queue.push(next);
    }
  }
  return order.length === ids.length ? order : null;
}

/** Há ciclo no grafo de dependências? */
export function hasCycle(tasks) {
  return topoOrder(tasks) === null;
}

/** @param {string} [status] */
function isGreen(status) {
  return GREEN_STATUS.includes(status);
}
function isTerminal(status) {
  return TERMINAL_STATUS.includes(status);
}

/**
 * Tasks PRONTOS pra rodar agora: não-terminais, não-running, e com TODAS as
 * dependências em status green. Preserva a ordem de entrada (estável).
 * @param {Array<{id:string,depends_on?:string[],status?:string,_serialOnly?:boolean}>} tasks
 */
export function nextRunnable(tasks) {
  const byId = indexById(tasks);
  const list = Array.isArray(tasks) ? tasks : [];
  return list.filter((t) => {
    if (!t || !byId.has(t.id)) return false;
    if (isTerminal(t.status) || t.status === 'running' || t.status === 'in_progress') return false;
    return depsOf(t, byId).every((d) => isGreen(byId.get(d)?.status));
  });
}

/**
 * Ondas de execução: cada wave é o conjunto de tasks SEM dependências entre si
 * cujas deps já foram satisfeitas por waves anteriores. Útil pra fan-out
 * paralelo respeitando o DAG. Retorna `Array<Task[]>` ou `null` se houver ciclo.
 * Não olha `status` — é o plano de execução IDEAL do grafo inteiro.
 */
export function waves(tasks) {
  const byId = indexById(tasks);
  if (hasCycle(tasks)) return null;
  const done = new Set();
  const result = [];
  const remaining = new Set(byId.keys());
  while (remaining.size) {
    const wave = [];
    for (const id of remaining) {
      const ok = depsOf(byId.get(id), byId).every((d) => done.has(d));
      if (ok) wave.push(id);
    }
    if (wave.length === 0) return null; // segurança (não deve ocorrer sem ciclo)
    for (const id of wave) { done.add(id); remaining.delete(id); }
    result.push(wave.map((id) => byId.get(id)));
  }
  return result;
}

/**
 * Valida o grafo pós-decomposição: ids únicos, deps referenciam ids existentes
 * (refs órfãs viram aviso, não erro), e ausência de ciclo. Retorna
 * { ok, cycle, orphanRefs, duplicateIds }.
 */
export function validateGraph(tasks) {
  const list = Array.isArray(tasks) ? tasks : [];
  const seen = new Set();
  const duplicateIds = [];
  for (const t of list) {
    if (!t || typeof t.id !== 'string') continue;
    if (seen.has(t.id)) duplicateIds.push(t.id);
    seen.add(t.id);
  }
  const orphanRefs = [];
  for (const t of list) {
    for (const d of Array.isArray(t?.depends_on) ? t.depends_on : []) {
      if (!seen.has(d)) orphanRefs.push({ task: t.id, missingDep: d });
    }
  }
  const cycle = hasCycle(list);
  return { ok: !cycle && duplicateIds.length === 0, cycle, orphanRefs, duplicateIds };
}
